// Per-strategy action buttons: activate/pause, test-fire (long/short), delete via
// a shadcn DropdownMenu so the card stays compact. Toast on success/error.

import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Play,
  Pause,
  Trash2,
  Zap,
  MoreHorizontal,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import type { StrategyDto } from "../../api/types";
import { deleteStrategy, patchStrategyStatus, testFireStrategy } from "../../api/strategies";

interface Props {
  strategy: StrategyDto;
  onChanged: () => void;
}

export function StrategyActions({ strategy, onChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const isActive = strategy.status === "active";

  const wrap = (label: string, action: () => Promise<unknown>) => async () => {
    setBusy(true);
    try {
      await action();
      toast.success(label);
      onChanged();
    } catch (err) {
      toast.error(`${label} failed`, { description: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const handleTestFire = (direction: "long" | "short") => async () => {
    setBusy(true);
    try {
      const r = await testFireStrategy(strategy.id, direction);
      toast.success(`Test-fired ${direction}`, {
        description: `fingerprint: ${r.fingerprint.split(":")[1]?.slice(0, 19) ?? "—"}`,
      });
      onChanged();
    } catch (err) {
      toast.error(`Test-fire ${direction} failed`, { description: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        size="sm"
        variant={isActive ? "outline" : "default"}
        disabled={busy}
        onClick={wrap(
          isActive ? "Paused" : "Activated",
          () => patchStrategyStatus(strategy.id, isActive ? "paused" : "active"),
        )}
        className={isActive ? "" : "bg-primary hover:bg-primary/90 text-primary-foreground"}
      >
        {isActive ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
        {isActive ? "Pause" : "Activate"}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" disabled={busy} aria-label="More actions">
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs">Test-fire (dev)</DropdownMenuLabel>
          <DropdownMenuItem onClick={handleTestFire("long")}>
            <Zap className="size-3.5 text-emerald-500" />
            <ArrowUpRight className="size-3.5" />
            Test-fire Long
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleTestFire("short")}>
            <Zap className="size-3.5 text-red-500" />
            <ArrowDownRight className="size-3.5" />
            Test-fire Short
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive focus:bg-destructive/10"
            onClick={wrap("Deleted", () => deleteStrategy(strategy.id))}
          >
            <Trash2 className="size-3.5" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
